import { apiFetch } from '@lib/apiFetch';
import type { components } from '@type/api/apiSchema';

export type ResumeInterviewAnsweredSummary = components['schemas']['ResumeInterviewAnsweredSummary'];

/**
 * Drop the backend's generic prepended warmup question (type "start" at index 0) from a
 * generate-question response, so callers only see the real questions to answer.
 *
 * Filtering by `type` alone is unsafe: the LLM's own generated questions can also legitimately
 * be tagged "start" (its own opener). The backend only ever prepends its own copy when that
 * pushes the total past 3, so only drop index 0 when there's an actual 4th item to explain it.
 */
export function filterRealInterviewQuestions<T extends { type: string; question: string }>(
  questions: T[],
): T[] {
  return questions.length > 3 && questions[0]?.type === 'start' ? questions.slice(1) : questions;
}

/**
 * POST /v1/resume/interview/answer { question, answer }
 * Saves a single general resume-interview text answer directly (no agent conversation) and
 * returns the refreshed answered summary so the caller can update the banner/button gate.
 */
export async function saveResumeInterviewAnswer(
  question: string,
  answer: string,
  locale: Locale = 'ro',
): Promise<ResumeInterviewAnsweredSummary> {
  const { data, error } = await apiFetch('/v1/resume/interview/answer', {
    method: 'post',
    locale,
    isClient: true,
    // apiFetch stringifies the payload but does not set Content-Type; without it the backend's
    // MapRequestPayload can't detect JSON and 500s ("Unsupported format: txt").
    headers: { 'Content-Type': 'application/json' },
    payload: { question, answer },
  });
  if (error || !data) {
    throw new Error(`saveResumeInterviewAnswer failed: ${error ? JSON.stringify(error) : 'no data'}`);
  }
  return data;
}
